/**
 * 언어 선택 — 검색 가능한 목록 + 실측 등급 배지.
 *
 * 목록이 19개다. 이전의 4개짜리 Select 를 그대로 늘리면 스크롤로 훑어야 하고
 * "튀르키예어" 를 찾으려면 눈으로 내려가야 한다. `Combobox` 는 타이핑 검색을
 * 내장하므로 그대로 쓴다(직접 만들지 않는다 — @cupia/ui 에 이미 있다).
 *
 * 배지의 뜻: **고를 수 있는가**가 아니라 **우리가 재 봤는가**다. 모델이
 * 공식 지원하는 언어는 전부 고를 수 있고, 등급은 우리 실측 상태만 덧붙인다.
 * 사용자가 목록에서 언어를 찾지 못해 회의를 못 여는 상황을 만들지 않으면서도,
 * 무엇이 검증됐는지는 숨기지 않는다.
 *
 * 등급 자체는 `server/languages.py` 가 정본이고 2단계 경로 실측(§2.1)에
 * 근거한다 — 목록에서 배지를 다는 규칙은 아래 `itemLabel` 주석 참조.
 */

import * as React from 'react'
import { Combobox } from '@cupia/ui/combobox'
import { TIER_HINT, TIER_LABEL, type Language } from '../lib/languages'

export interface LanguageSelectProps {
  /** 선택지 — 보통 SOURCE_LANGUAGES 또는 TARGET_LANGUAGES */
  options: readonly Language[]
  value: string
  onChange: (value: string) => void
  /** 접근성 이름 — 시각적 라벨은 바깥 Field 가 그린다 */
  label: string
  disabled?: boolean
}

/**
 * 항목 라벨 — "베트남어 (vi)" 뒤에 **제한적**일 때만 등급을 붙인다.
 *
 * 원칙은 예전과 같다: **예외 표시가 정보다.** 바뀐 것은 무엇이 예외인가다.
 *
 * 예전에는 19개가 전부 `verified` 라 "검증 안 됨" 이 예외였고, 그래서
 * `verified` 만 배지를 뺐다. 지금은 2단계 경로 실측 기준으로 `verified` 가
 * ko·vi·en 셋뿐이다(docs/LANGUAGES.md §2.1). 같은 규칙을 그대로 두면 16개에
 * 배지가 붙어 **배지가 곧 배경**이 되고, 정작 눈에 띄어야 할 "제한적"(현재
 * th 하나)이 그 속에 묻힌다 — 옛 주석이 경고하던 바로 그 상태다.
 *
 * 그래서 다수인 `supported` 도 배지를 달지 않는다. 이건 정보를 숨기는 게
 * 아니다: `supported` 는 강등이 아니라 "아직 못 쟀다" 이고, 고른 뒤에는
 * 아래 설명 줄이 사유를 그대로 보여 준다. 목록에서 시선을 끌어야 하는 것은
 * **문제가 관측된 언어 하나**뿐이다.
 */
export function itemLabel(lang: Language): string {
  const base = `${lang.ko} (${lang.code})`
  return lang.tier === 'limited' ? `${base} · ${TIER_LABEL[lang.tier]}` : base
}

export function LanguageSelect({
  options,
  value,
  onChange,
  label,
  disabled,
}: LanguageSelectProps): React.ReactElement {
  const items = React.useMemo(
    () => options.map((l) => ({ value: l.code, label: itemLabel(l) })),
    [options],
  )
  // 선택 항목은 **넘겨받은 options 안에서** 찾는다. 전역 목록을 뒤지면
  // 이 선택기에 없는 언어의 설명을 그릴 수 있다(원언어 전용 언어를 타깃
  // 선택기가 설명하는 식).
  const selected = React.useMemo(
    () => options.find((l) => l.code === value),
    [options, value],
  )

  return (
    <div className="flex flex-col gap-1">
      <Combobox
        items={items}
        value={value}
        onChange={onChange}
        disabled={disabled}
        aria-label={label}
        placeholder="언어 선택"
        searchPlaceholder="언어 검색…"
        emptyText="해당 언어가 없습니다"
      />
      {/* 고른 언어가 검증되지 않았으면 **고른 뒤에** 사유를 보여 준다.
          목록에서 `supported` 는 배지를 달지 않으므로(위 itemLabel), 사용자가
          실측 상태를 알 수 있는 자리는 사실상 여기뿐이다. 한 줄이라 목록을
          어지럽히지 않으면서, 고른 언어에 대해서만 정확히 말해 준다. */}
      {selected && selected.tier !== 'verified' ? (
        <p className="text-(length:--text-xs-size) text-[var(--color-muted-foreground)]">
          {selected.note || TIER_HINT[selected.tier]}
        </p>
      ) : null}
    </div>
  )
}
